// Team Member Details Dialog Component
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Mail,
  Phone,
  Building2,
  Briefcase,
  User,
  Clock,
  CheckCircle2,
  Info,
  Loader2,
  RotateCcw,
  Calendar,
  Copy,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
  TrendingUp,
  Settings,
  Shield,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmployeeImage } from '@/components/EmployeeImage';
import { EmployeeGradeBadge } from '@/components/EmployeeGradeBadge';
import { WorkingPatternForm } from '@/components/WorkingPatternForm';
import { WorkingPatternDisplay } from '@/components/WorkingPatternDisplay';
import { WorkflowAssignmentCard } from '@/components/WorkflowAssignmentCard';
import { WorkflowSelectionModal } from '@/components/WorkflowSelectionModal';
import { CompanyAccessTab } from '@/components/employees/CompanyAccessTab';
import type { Employee, WorkingPattern } from '@/types/timesheet';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { useWorkflowAssignment } from '@/hooks/useWorkflowAssignment';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { getEmployeeRates, type EmployeeRates } from '@/services/employeeRatesService';
import { formatCurrency } from '@/utils/formatters';

interface TeamMember extends Employee {
  status: 'active' | 'inactive' | 'on-leave';
  phone?: string;
  location?: string;
  startDate?: Date;
}

interface TeamMemberDetailsDialogProps {
  member: TeamMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMemberUpdate?: (member: TeamMember) => void;
  getStatusColor: (status: 'active' | 'inactive' | 'on-leave') => string;
  formatDate: (date: Date) => string;
}

export const TeamMemberDetailsDialog: React.FC<TeamMemberDetailsDialogProps> = ({
  member,
  open,
  onOpenChange,
  onMemberUpdate,
  getStatusColor,
  formatDate,
}) => {
  const { mode } = useScreenDataMode('team-members');
  
  const [workingPattern, setWorkingPattern] = useState<WorkingPattern | null>(null);
  const [isInherited, setIsInherited] = useState(true);
  const [patternId, setPatternId] = useState<string | undefined>(undefined);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);

  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [employeeRates, setEmployeeRates] = useState<EmployeeRates | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);

  // Workflow assignment hook
  const {
    assignment: workflowAssignment,
    isLoading: isLoadingWorkflow,
    assignWorkflow,
    revertToDefault: revertWorkflowToDefault,
  } = useWorkflowAssignment(member?.id || null);

  useEffect(() => {
    if (member && open) {
      loadWorkingPattern();
      loadEmployeeRates();
    }
  }, [member, open, mode]);

  const loadWorkingPattern = async () => {
    if (!member) return;
    
    setIsLoading(true);
    
    try {
      const result = await settingsRepository.getEmployeeWorkingPattern(member.id, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
    } catch (err) {
      console.error('Error loading working pattern:', err);
      toast.error('Failed to load working pattern', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePattern = async (pattern: WorkingPattern) => {
    if (!member) return;
    
    setIsSaving(true);
    
    try {
      const result = await settingsRepository.setEmployeeWorkingPattern(member.id, pattern, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      
      toast.success('Working pattern saved', {
        description: `${member.name}'s pattern set to ${pattern.hoursPerWeek} hours per week`,
      });
      
      // Update the parent component's state with the new working pattern
      if (onMemberUpdate) {
        onMemberUpdate({
          ...member,
          workingPattern: result.pattern,
        });
      }
      
    } catch (err) {
      console.error('Error saving working pattern:', err);
      toast.error('Failed to save working pattern', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToDefault = async () => {
    if (!member) return;
    
    setIsSaving(true);
    
    try {
      await settingsRepository.deleteEmployeeWorkingPattern(member.id, patternId, mode);
      
      const result = await settingsRepository.getEmployeeWorkingPattern(member.id, mode);
      setWorkingPattern(result.pattern);
      setIsInherited(result.inherited);
      setPatternId(result.id);
      
      toast.success('Reverted to organization default', {
        description: `${member.name} now uses the organization default working pattern`,
      });
      
      // Update the parent component's state with the default working pattern
      if (onMemberUpdate) {
        onMemberUpdate({
          ...member,
          workingPattern: result.pattern,
        });
      }
      
    } catch (err) {
      console.error('Error reverting to default:', err);
      toast.error('Failed to revert to default', {
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const loadEmployeeRates = async () => {
    if (!member) return;
    setIsLoadingRates(true);
    try {
      const rates = await getEmployeeRates(member.id, mode);
      setEmployeeRates(rates);
    } catch (err) {
      console.error('Error loading employee rates:', err);
      setEmployeeRates(null);
    } finally {
      setIsLoadingRates(false);
    }
  };

  const handleCopyEmail = () => {
    if (member?.email) {
      navigator.clipboard.writeText(member.email);
    }
  };

  const handleCopyPhone = () => {
    if (member?.phone) {
      navigator.clipboard.writeText(member.phone);
    }
  };

  const getWorkingPatternSummary = () => {
    if (!workingPattern) return null;
    
    const activeDays = Object.entries(workingPattern.workingDays)
      .filter(([_, isWorking]) => isWorking)
      .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3));
    
    return {
      hoursPerWeek: workingPattern.hoursPerWeek,
      daysWorking: activeDays.length,
      schedule: activeDays.join(', '),
      isInherited,
    };
  };

  if (!member) return null;

  const workingPatternSummary = getWorkingPatternSummary();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl lg:max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="sr-only">
          <SheetTitle>Team Member Details</SheetTitle>
          <SheetDescription>
            View and manage team member information and settings
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col h-full">

        {/* Success Message */}
        <div className="p-6 space-y-6">
        {showSaveSuccess && (
          <Alert className="border-success-500 bg-success-500/10">
            <CheckCircle2 className="h-4 w-4 text-success-600" />
            <AlertDescription className="text-success-600">
              Working pattern saved successfully
            </AlertDescription>
          </Alert>
        )}

        {/* Simplified Header - Phase 1 Optimization */}
        <div className="flex items-start gap-4 pb-4 border-b">
          <div className="relative flex-shrink-0">
            <EmployeeImage
              name={member.name}
              photoUrl={member.photoUrl}
              size="lg"
            />
            {member.status === 'active' && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-success-500 rounded-full border-2 border-background" />
            )}
          </div>
          
          <div className="flex-1 min-w-0 space-y-2">
            {/* Name and Role */}
            <div>
              <h2 className="text-xl font-bold tracking-tight">{member.name}</h2>
              <p className="text-sm text-muted-foreground">{member.role}</p>
            </div>
            
            {/* Status Badge Only */}
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn("font-medium", getStatusColor(member.status))}
              >
                {member.status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Tabs: Details | Company Access */}
        <Tabs defaultValue="details" className="mt-2">
          <TabsList className="w-full rounded-none border-b bg-transparent px-6 justify-start gap-0 h-auto p-0">
            <TabsTrigger
              value="details"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
            >
              Details
            </TabsTrigger>
            <TabsTrigger
              value="company-access"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
            >
              <Building2 className="h-3.5 w-3.5 mr-1.5" />
              Company Access
            </TabsTrigger>
          </TabsList>

          {/* ── Details Tab ── */}
          <TabsContent value="details" className="mt-0 px-6 pb-6">
        <div className="space-y-4 mt-4">
            <div className="grid gap-4">
              {/* Profile Card - Merged Contact + Work Information */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {/* Left Column - Contact */}
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Contact
                      </div>
                      <div className="flex items-start gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground">Email</p>
                          <a 
                            href={`mailto:${member.email}`}
                            className="text-sm font-medium text-primary hover:underline break-all"
                          >
                            {member.email}
                          </a>
                        </div>
                      </div>
                      {member.phone && (
                        <div className="flex items-start gap-2">
                          <Phone className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">Phone</p>
                            <a 
                              href={`tel:${member.phone}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {member.phone}
                            </a>
                          </div>
                        </div>
                      )}
                      {member.location && (
                        <div className="flex items-start gap-2">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">Location</p>
                            <p className="text-sm font-medium">{member.location}</p>
                          </div>
                        </div>
                      )}
                      {member.userRoleName && (
                        <div className="flex items-start gap-2">
                          <Shield className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">User Role</p>
                            <p className="text-sm font-medium">{member.userRoleName}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Work */}
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Work
                      </div>
                      <div className="flex items-start gap-2">
                        <Briefcase className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground">Role</p>
                          <p className="text-sm font-medium">{member.role}</p>
                        </div>
                      </div>
                      {member.grade && (
                        <div className="flex items-start gap-2">
                          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">Grade</p>
                            <div className="mt-1">
                              <EmployeeGradeBadge grade={member.grade} gradeLevel={member.gradeLevel} size="sm" />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-muted-foreground">Department</p>
                          <p className="text-sm font-medium">{member.department}</p>
                        </div>
                      </div>
                      {member.managerName && (
                        <div className="flex items-start gap-2">
                          <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">Manager</p>
                            <p className="text-sm font-medium">{member.managerName}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Rates */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Rates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingRates ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                  ) : employeeRates && (employeeRates.costRatePerHour !== null || employeeRates.billRatePerHour !== null) ? (
                    <div className="space-y-4">
                      {(employeeRates.costRatePerDay !== null || employeeRates.costRatePerHour !== null) && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Cost Rate</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Day Rate</p>
                              <p className="text-lg font-semibold">
                                {employeeRates.costRatePerDay !== null
                                  ? formatCurrency(employeeRates.costRatePerDay, employeeRates.currency)
                                  : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Hour Rate</p>
                              <p className="text-lg font-semibold">
                                {employeeRates.costRatePerHour !== null
                                  ? formatCurrency(employeeRates.costRatePerHour, employeeRates.currency)
                                  : '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {(employeeRates.billRatePerDay !== null || employeeRates.billRatePerHour !== null) && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Bill Rate</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Day Rate</p>
                              <p className="text-lg font-semibold">
                                {employeeRates.billRatePerDay !== null
                                  ? formatCurrency(employeeRates.billRatePerDay, employeeRates.currency)
                                  : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Hour Rate</p>
                              <p className="text-lg font-semibold">
                                {employeeRates.billRatePerHour !== null
                                  ? formatCurrency(employeeRates.billRatePerHour, employeeRates.currency)
                                  : '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {employeeRates.effectiveFrom && (
                        <p className="text-xs text-muted-foreground">
                          Effective from {new Date(employeeRates.effectiveFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No rate information available</p>
                  )}
                </CardContent>
              </Card>

              {/* Working Pattern Summary */}
              {workingPatternSummary && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Working Pattern
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <WorkingPatternDisplay
                      workingDays={workingPattern ? (() => {
                        const dayNames: { key: keyof typeof workingPattern.workingDays; day: string; dayShort: string }[] = [
                          { key: 'monday', day: 'Monday', dayShort: 'Mon' },
                          { key: 'tuesday', day: 'Tuesday', dayShort: 'Tue' },
                          { key: 'wednesday', day: 'Wednesday', dayShort: 'Wed' },
                          { key: 'thursday', day: 'Thursday', dayShort: 'Thu' },
                          { key: 'friday', day: 'Friday', dayShort: 'Fri' },
                          { key: 'saturday', day: 'Saturday', dayShort: 'Sat' },
                          { key: 'sunday', day: 'Sunday', dayShort: 'Sun' },
                        ];
                        const workingDayCount = Object.values(workingPattern.workingDays).filter(Boolean).length;
                        const hoursPerDay = workingDayCount > 0 ? workingPattern.hoursPerWeek / workingDayCount : 0;
                        return dayNames.map(({ key, day, dayShort }) => {
                          const isWorking = workingPattern.workingDays[key];
                          const hours = workingPattern.dailyHours?.[key] ?? (isWorking ? hoursPerDay : 0);
                          return { day, dayShort, isWorking, hours };
                        });
                      })() : undefined}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Authorisation Workflow Assignment */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Authorisation Workflow
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <WorkflowAssignmentCard
                    assignment={workflowAssignment}
                    isLoading={isLoadingWorkflow}
                    onChangeWorkflow={() => setShowWorkflowModal(true)}
                    onRevertToDefault={async () => {
                      await revertWorkflowToDefault('Reverting to organization default');
                    }}
                    employeeName={member.name}
                  />
                </CardContent>
              </Card>

            </div>
        </div>
          </TabsContent>

          {/* ── Company Access Tab ── */}
          <TabsContent value="company-access" className="mt-0 px-6 pb-6 pt-4">
            <CompanyAccessTab
              employeeId={String(member.id)}
              employeeName={member.name}
            />
          </TabsContent>
        </Tabs>

        </div>
        </div>
      </SheetContent>

      {/* Workflow Selection Modal */}
      <WorkflowSelectionModal
        open={showWorkflowModal}
        onOpenChange={setShowWorkflowModal}
        currentWorkflowId={workflowAssignment?.workflowId}
        employeeName={member.name}
        onSelect={async (workflowId, reason) => {
          const success = await assignWorkflow({ workflowId, reason });
          return success;
        }}
      />

      {/* Revert Confirmation Dialog */}
      <AlertDialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-500" />
              Revert to Organization Default?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p>
                This will remove {member?.name}'s custom working pattern and restore the organization default pattern.
              </p>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  This action cannot be undone. The custom pattern will be permanently deleted.
                </AlertDescription>
              </Alert>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowRevertDialog(false);
                handleRevertToDefault();
              }}
              disabled={isSaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reverting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Revert to Default
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
};
